import { FormikErrors, FormikTouched } from 'formik'
import {
  ENTITY_TYPES,
  type IEmailCollectionItem,
  isValidPhone,
  isValidPhoneExtension,
  LocationDTO,
  updatePhoneNumber,
} from '@sales-center/common'
import { LocationInput } from '@user/store/entityLocations'
import { FormAddress } from '@user/components/Formik'
import { validateEmailCollection } from '@user/components/EmailCollection/'
import { formatEmailAddresses } from '@user/utils/formatEmailAddresses'
import { locationUnder30PP } from '@user/components/Settings/EntityFormSettings'

export type AddServiceLocationFormModel = FormAddress & {
  id?: number
  contactId?: number
  locationTypeId: string
  locationType: string
  divisionId: string
  division: string
  firstName: string
  lastName: string
  company: string
  emailAddresses: IEmailCollectionItem[]
  phoneNumber: string
  phoneExt: string
  altPhoneNumber: string
  altPhoneExt: string
  updateBillTo: boolean
  billingLocation: boolean
  branchId: string
  branch: string
  contactMethod: string
  contactTime: string[]
}

export function setInitialTouchedServiceLocationForm(
  initialErrors: FormikErrors<AddServiceLocationFormModel>,
): FormikTouched<AddServiceLocationFormModel> {
  let touched: FormikTouched<AddServiceLocationFormModel> = {}
  touched = Object.entries(initialErrors).reduce(
    (p, [k]) => ({ ...p, [k]: true }),
    {},
  )

  return touched
}

export function validateServiceLocationForm(
  values: AddServiceLocationFormModel,
  runPestPacValidation: boolean,
  validatePostalCode: (postalCode: string) => string | undefined,
): FormikErrors<AddServiceLocationFormModel> {
  const {
    firstName,
    lastName,
    company,
    phoneNumber,
    phoneExt,
    branchId,
    altPhoneNumber,
    altPhoneExt,
    emailAddresses,
    address1,
    city,
    state,
    postalCode,
  } = values

  const errors: FormikErrors<AddServiceLocationFormModel> = {}

  const contactName = firstName !== '' || lastName !== '' || company !== ''

  if (!contactName) {
    errors.firstName = 'Required'
    errors.lastName = 'Required'
    errors.company = 'Required'
  }

  if (runPestPacValidation) {
    if (address1 === '') errors.address1 = 'Required'
    if (city === '') errors.city = 'Required'
    if (state === '') errors.state = 'Required'
    if (postalCode === '') errors.postalCode = 'Required'
  }

  const hasEmail = emailAddresses.find((x) => x.emailAddress !== '')
  const hasPhone = phoneNumber !== ''
  const emailRequired = !hasPhone && !hasEmail
  const phoneRequired = !hasEmail

  if (emailRequired || hasEmail) {
    const emailErrors = validateEmailCollection(
      emailAddresses,
      emailRequired,
      runPestPacValidation,
      true,
    )
    if (emailErrors) errors.emailAddresses = emailErrors
  }

  if (phoneRequired && !hasPhone) {
    errors.phoneNumber = 'Required'
  } else if (hasPhone && !isValidPhone(phoneNumber)) {
    errors.phoneNumber = 'Must be valid phone number'
  }

  if (phoneExt !== '' && !isValidPhoneExtension(phoneExt))
    errors.phoneExt = 'Invalid'

  if (phoneExt !== '' && phoneExt?.length > 5)
    errors.phoneExt = 'Phone extension must be between 1 and 5 characters'

  if (altPhoneNumber !== '' && !isValidPhone(altPhoneNumber)) {
    errors.altPhoneNumber = 'Must be valid phone number'
  }

  if (phoneNumber.trim() !== '' && altPhoneNumber.trim() !== '') {
    if (phoneNumber?.trim() === altPhoneNumber?.trim()) {
      errors.altPhoneNumber = 'Must be different from primary phone'
    }
  }

  if (altPhoneExt !== '' && !isValidPhoneExtension(altPhoneExt))
    errors.altPhoneExt = 'Invalid'

  if (altPhoneExt !== '' && altPhoneExt?.length > 5)
    errors.altPhoneExt = 'Phone extension must be between 1 and 5 characters'

  if (branchId === '') errors.branchId = 'Required'
  if (runPestPacValidation) validatePestPacMatched(values, errors)

  if (postalCode.trim() !== '') {
    const errorPostal = validatePostalCode(postalCode)
    if (errorPostal) errors.postalCode = errorPostal
  }
  return errors
}

export function validateExternalServiceLocationForm(
  values: AddServiceLocationFormModel,
  runPestPacValidation: boolean,
  validatePostalCode: (postalCode: string) => string | undefined,
): FormikErrors<AddServiceLocationFormModel> {
  const { address1, city, state, postalCode } = values

  const errors = validateServiceLocationForm(
    values,
    runPestPacValidation,
    validatePostalCode,
  )

  if (address1.trim() === '') errors.address1 = 'Required'
  if (city.trim() === '') errors.city = 'Required'
  if (state.trim() === '') errors.state = 'Required'
  if (postalCode.trim() === '') errors.postalCode = 'Required'

  return errors
}

export const defaultValues: AddServiceLocationFormModel = {
  branchId: '',
  branch: '',
  locationTypeId: '',
  locationType: '',
  divisionId: '',
  division: '',
  firstName: '',
  lastName: '',
  company: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  postalCode: '',
  county: '',
  country: '',
  lat: 0,
  lng: 0,
  emailAddresses: [
    {
      emailAddress: '',
      isPrimary: true,
    },
  ],
  phoneNumber: '',
  phoneExt: '',
  altPhoneNumber: '',
  altPhoneExt: '',
  updateBillTo: true,
  billingLocation: false,
  contactMethod: '',
  contactTime: [],
}

export function locationToFormModel(
  location: LocationDTO,
): AddServiceLocationFormModel {
  const {
    id,
    locationTypeId,
    locationType,
    divisionId,
    division,
    firstName,
    lastName,
    company,
    street1,
    street2,
    city,
    stateAbbreviation,
    postalCode,
    county,
    country,
    latitude,
    longitude,
    emailAddresses,
    phoneNumber,
    phoneExt,
    altPhoneNumber,
    altPhoneExt,
    billingLocation,
    branchId,
    branch,
    contact,
    mobilePhone,
    locationCode,
    externalLinkId,
  } = location

  return {
    id,
    branchId: !branchId || branchId === 0 ? '' : `${branchId}`,
    branch: branch ?? '',
    locationTypeId:
      !locationTypeId || locationTypeId === '' ? '' : locationTypeId,
    locationType: locationType ?? '',
    divisionId: !divisionId || divisionId === '0' ? '' : divisionId,
    division: division ?? '',
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    company: company ?? '',
    address1: street1 ?? '',
    address2: street2 ?? '',
    city: city ?? '',
    state: stateAbbreviation ?? '',
    postalCode: postalCode ?? '',
    county: county ?? '',
    country: country ?? '',
    lat: latitude ?? 0,
    lng: longitude ?? 0,
    phoneNumber: phoneNumber ?? '',
    phoneExt: phoneExt ?? '',
    altPhoneNumber:
      altPhoneNumber && altPhoneNumber.length > 0
        ? altPhoneNumber
        : Boolean(externalLinkId) &&
            Boolean(locationCode) &&
            Boolean(mobilePhone)
          ? mobilePhone!
          : '',
    altPhoneExt: altPhoneExt ?? '',
    updateBillTo: billingLocation ? false : true,
    billingLocation,
    emailAddresses: emailAddresses,
    contactMethod: contact?.contactMethod ?? '',
    contactTime: contact?.contactTime ? contact.contactTime.split(',') : [],
  }
}

export function formModelToLocationInput(
  model: AddServiceLocationFormModel,
  entityId: number,
  entityType: ENTITY_TYPES,
  externalLocationId?: string | null,
): LocationInput {
  return {
    ...model,
    externalLocationId: externalLocationId,
    entityId,
    entityType,
    updateOnlyParentProduct: entityId === -1 || model.id == -1,

    contact: {
      ...model,
      id: model.contactId,
      street1: model.address1,
      street2: model.address2,
      stateAbbreviation: model.state,
      county: model.county,
      latitude: model.lat,
      longitude: model.lng,
      emailAddresses: formatEmailAddresses(model.emailAddresses),
      contactTime: model.contactTime ? model.contactTime.join(',') : undefined,
    },
  }
}

export function validatePestPacMatched(
  values: AddServiceLocationFormModel,
  errors: FormikErrors<AddServiceLocationFormModel>,
): void {
  const {
    firstName,
    lastName,
    company,
    address1,
    address2,
    state,
    postalCode,
    phoneNumber,
    phoneExt,
    altPhoneNumber,
    altPhoneExt,
  } = values

  if (firstName.trim() !== '' && firstName.length > 15)
    errors.firstName = 'First name must be between 1 and 15 characters'
  if (lastName.trim() !== '' && lastName.length > 20)
    errors.lastName = 'Last name must be between 1 and 20 characters'
  if (company.trim() !== '' && company.length > 35)
    errors.company = 'Company must be between 1 and 35 characters'
  if (address1.trim() !== '' && address1.length > 30)
    errors.address1 = locationUnder30PP
  if (address2.trim() !== '' && address2.length > 30)
    errors.address2 = locationUnder30PP
  if (state.trim() !== '' && state.length > 10)
    errors.state = 'State must be between 1 and 10 characters'
  if (postalCode.trim() !== '' && postalCode.length > 10)
    errors.postalCode = 'Postal code must be between 1 and 10 characters'
  if (phoneNumber.trim() !== '' && updatePhoneNumber(phoneNumber).length > 12)
    errors.phoneNumber = 'Phone number must be between 1 and 12 characters'
  if (phoneExt.trim() !== '' && phoneExt.length > 5)
    errors.phoneExt = 'Phone extension must be between 1 and 5 characters'
  if (
    altPhoneNumber.trim() !== '' &&
    updatePhoneNumber(altPhoneNumber).length > 12
  )
    errors.altPhoneNumber =
      'Alt Phone number must be between 1 and 12 characters'
  if (altPhoneExt.trim() !== '' && altPhoneExt.length > 5)
    errors.altPhoneExt =
      'Alt Phone extension must be between 1 and 5 characters'
}
